import { dateOf, esc } from './sbchart.ts';

type J = Record<string, any>;
export type DeathCursor = { day: number; id: number };
const MAX = 0xffff_ffff;
const valid = (x: unknown): x is number => typeof x === 'number' && Number.isSafeInteger(x) && x >= 0 && x <= MAX;
const before = (a: DeathCursor, b: DeathCursor) => a.day < b.day || a.day === b.day && a.id < b.id;

/** 一页结果、最多32个回退游标。仅显式翻页/刷新失效,城市日推进不触发扫描。 */
export function createDeathDirectory() {
  let generation: number | null = null, cursor: DeathCursor = { day: MAX, id: MAX }, stack: DeathCursor[] = [], loaded = false, page: J | null = null;
  const ensure = (gen: number) => { if (generation !== gen) { generation = gen; cursor = { day: MAX, id: MAX }; stack = []; loaded = false; page = null; } };
  return {
    cursor(gen: number) { ensure(gen); return { ...cursor }; },
    get(gen: number, load: (cursor: DeathCursor) => J | null) {
      ensure(gen);
      if (!loaded) { const value = load({ ...cursor }); if (generation !== gen) return null; page = value; loaded = true; }
      return page;
    },
    canPrevious(gen: number) { ensure(gen); return stack.length > 0; },
    navigate(gen: number, action: string) {
      ensure(gen);
      if (action === 'next') {
        const next = page?.nextBefore;
        if (!page?.hasMore || !valid(next?.day) || !valid(next?.id) || !before(next, cursor)) return false;
        stack.push(cursor); if (stack.length > 32) stack.shift(); cursor = { day: next.day, id: next.id };
      } else if (action === 'previous') { if (!stack.length) return false; cursor = stack.pop()!; }
      else if (action === 'latest') { cursor = { day: MAX, id: MAX }; stack = []; }
      else if (action !== 'refresh') return false;
      loaded = false; page = null; return true;
    },
  };
}

export function recentDeathsHTML(page: J | null, canPrevious: boolean): string {
  if (!page) return '<div class="sb-empty">当前版本未提供可读取的近期死亡目录；个人档案仍保留已有死亡记录。</div>';
  const rows: J[] = Array.isArray(page.rows) ? page.rows.slice(0, 80) : [];
  const count = (x: unknown) => valid(x) ? String(x) : '未知';
  return `<p><b>近期死亡记录</b> · 数据截至 ${valid(page.day) ? dateOf(page.day) : '日期未记录'}</p>` +
    `<p class="meta">已观察死亡快照 ${count(page.total)} 条；本页 ${rows.length} 条，上限80。旧资料另有 ${count(page.unknownCount)} 人死亡但没有死亡快照，无法按实际死亡日排序。此页只在打开、翻页或手动刷新时读取，不自动追随推演。</p>` +
    `<div class="sb-history-controls"><button data-medical-deaths="previous"${canPrevious ? '' : ' disabled'}>较新一页</button><button data-medical-deaths="next"${page.hasMore ? '' : ' disabled'}>更早一页</button><button data-medical-deaths="refresh">刷新本页</button><button data-medical-deaths="latest">回最新记录</button></div>` +
    (page.error ? `<p class="bad">${esc(page.error)}</p>` : '') +
    (rows.length ? rows.map(r => {
      const person = valid(r.who?.id) ? `<a data-go="person:${r.who.id}" data-view="medical">${esc(r.who.name || `人物 #${r.who.id}`)} · 医疗与死亡详情 →</a>` : esc(r.who?.name || '人物编号未知');
      const age = typeof r.age === 'number' && Number.isFinite(r.age) && r.age >= 0 ? `${r.age.toFixed(1)} 岁` : '未记录';
      const diagnosed = (Array.isArray(r.diagnoses) ? r.diagnoses : []).map((d: J) => `${esc(d.name)}${valid(d.episode) ? `（病程 #${d.episode}）` : ''}${valid(d.hospital?.id) ? ` · <a data-go="hospital:${d.hospital.id}">${esc(d.hospital.name || `医院 #${d.hospital.id}`)}</a>` : ''}`).join('；');
      return `<article class="sb-sec"><h4>${valid(r.day) ? dateOf(r.day) : '死亡日未知'} · ${person}</h4><p>死亡年龄：${age} · 实际触发源：<b>${esc(r.trigger?.name || '未知')}</b>${r.trigger?.code === 'population_mixed_risk' ? '；单一医学死因未确认' : ''}</p><p class="meta">死亡时并存确诊（非死因）：${diagnosed || (r.medicalSnapshotKnown ? '没有保留确诊病名' : '医疗快照未知')}${r.unconfirmedEpisodes > 0 ? `；另有 ${count(r.unconfirmedEpisodes)} 段当时未确诊病程` : ''}。</p></article>`;
    }).join('') : '<div class="sb-empty">这一日期范围没有已观察的死亡快照。</div>') +
    '<p class="meta">日常混合风险不提供单一疾病归因；事故、命案来源也不直接等于责任认定。病程因死亡结束不等于医疗致死。人物目录含迁出后在外地死亡的人物，不能将此名单当作医院死亡名单；完整当时记录和关联案卷见本人详情。</p>';
}
